import { distance } from "fastest-levenshtein";
import type { GetTemplatesResponseType, TemplateType } from "@whatslly/types/GetTemplatesResponse";
import { dataUriToBase64 } from "@whatslly/commons/utils";
import { dataURItoBlob } from "@whatslly/commons/dataUriUtils";
import { webScriptBackend } from "@whatslly/instant-messaging/webScriptBackend";
import { BgCommands } from "@whatslly/commons/bgCommands";
import type { QueryResponseType } from "@whatslly/types/QueryResponse";
import type { InstantMessagingService } from "@whatslly/types/instant-messaging/InstantMessagingService";
import { IInstantMessagingServiceSymbol } from "@whatslly/types/instant-messaging/InstantMessagingService";
import { WebScriptCommands } from "@whatslly/commons/webScriptCommands";
import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable, reaction } from "mobx";
import { fromPromise } from "mobx-utils";
import type { PersonType } from "@whatslly/types/Person";
import type { UserType } from "@whatslly/types/User";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { SizeConstants } from "./constants/SizeConstants";
import type { IStorage } from "@whatslly/commons/storage.constants";
import { InjectStorage, STORAGE } from "@whatslly/commons/storage.constants";
import { getConfigFromLoginProvider } from "@whatslly/commons/login-provider/getConfigFromLoginProvider";
import njixTypes from "../../nginx-types.json";
import type { WhatsllyGroupType } from "@whatslly/types/WhatsllyGroup";
import { backgroundClient } from "@whatslly/commons/background/client";
import { AppIds } from "@whatslly/types/LoginProviderType";
import type { ISendMessage } from "@whatslly/instant-messaging/sendMessage";
import { InjectSendMessage } from "@whatslly/instant-messaging/sendMessage";
import type { IImTemplatesStore, TemplateStoreData } from "@whatslly/instant-messaging/commons/IImTemplatesStore";
import { formatTemplateMsg } from "@whatslly/instant-messaging/commons/IImTemplatesStore";
const TEMPLATE_SIMILARITY_THRESHOLD = 0.6;
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

const extractFilenameFromUrl = (url: string): string | undefined => {
  try {
    const pathname = new URL(url).pathname;
    const filename = decodeURIComponent(pathname.split("/").pop() ?? "");
    return filename || undefined;
  } catch {
    return undefined;
  }
};

@injectable()
export class TemplatesStore implements IImTemplatesStore {
  private person?: PersonType;
  private isGroup?: boolean;
  private group?: WhatsllyGroupType;
  private messageTemplateId = new Map<string, string>();
  private templatesCache = new Map<string, { request: Promise<GetTemplatesResponseType>; fetchedAt: number }>();
  private _containerSizeIsBig = false;
  private forceUpdate = 1;
  showOnlyOwnByMe = false;
  favoriteTemplates: Set<string> = new Set();
  user?: UserType;
  isOpen = false;
  copied = false;
  previewdTemplate?: TemplateType;
  templateFilesCache = new Map<string, File[]>();

  constructor(
    @inject(FrontLogger) private logger: FrontLogger,
    @inject(IInstantMessagingServiceSymbol) private readonly instantMessagingService: InstantMessagingService,
    @inject(InjectSendMessage) private readonly sendMessage: ISendMessage,
    @inject(InjectStorage) private readonly storage: IStorage
  ) {
    makeObservable(this, {
      isOpen: observable,
      copied: observable,
      user: observable.ref,
      person: observable.ref,
      isGroup: observable,
      group: observable.ref,
      previewdTemplate: observable,
      _containerSizeIsBig: observable,
      favoriteTemplates: observable,
      forceUpdate: observable,
      previewTemplate: computed,
      allowCreateTemplate: computed,
      allowEditTemplate: computed,
      templatesData: computed,
      containerSizeIsBig: computed,
      height: computed,
      setProps: action,
      toggleFavoriteTemplate: action,
      favoriteTemplatesInit: action,
      useTemplate: action.bound,
      onVisibilityChange: action.bound,
      openPreviewMsg: action.bound,
      closePreviewMsg: action.bound,
      closeTemplateWidget: action.bound,
      showOnlyOwnByMe: observable,
      setShowOnlyOwnByMe: action,
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    webScriptBackend.on(WebScriptCommands.TOGGLE_TEMPLATES, () => this.onToggle());
    reaction(
      () => this.height,
      (height) => this.updateWhatsappPeddingBottomElementHeight(height)
    );
    this.favoriteTemplatesInit();
  }

  setShowOnlyOwnByMe(val: boolean) {
    this.templatesCache.clear();
    this.templateFilesCache.clear();
    this.showOnlyOwnByMe = val;
  }
  setProps(props: { queryResponse: QueryResponseType }) {
    this.person = props.queryResponse?.success && !Array.isArray(props.queryResponse.person) ? props.queryResponse?.person : undefined;
    this.isGroup = props.queryResponse?.success ? props.queryResponse.isGroup : undefined;
    this.group = props.queryResponse?.success && props.queryResponse.group ? props.queryResponse.group : undefined;
    this.user = props.queryResponse.user;
  }

  dispose() {
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }

  private onVisibilityChange() {
    if (document.hidden) {
      return;
    }
    this.templatesCache.clear();
    this.templateFilesCache.clear();
    this.forceUpdate++;
  }

  private onToggle() {
    if (!this.isOpen) {
      this.templatesCache.delete(this.person?.Id || "");
      this.templateFilesCache.clear();
      backgroundClient.send(BgCommands.TRK, { action: "open-template-list" });
    }
    this.isOpen = !this.isOpen;
  }

  public async useTemplate(template: TemplateType) {
    backgroundClient.send(BgCommands.TRK, {
      action: "pick-template",
      traits: {
        templateId: template.Id,
      },
    });
    backgroundClient.send(BgCommands.ANALYTICS_TRACK, {
      schemaName: "user_interaction",
      schemaContent: {
        person_id: this.person?.Id,
        phone: this.instantMessagingService.getMe()?.phone,
        interaction_name: "use-template",
        entity_id: template.Id,
      },
    });
    const files = await this.getFiles(template.Id, template.templateFiles);
    await this.sendMessage.sendMessage(formatTemplateMsg(template.msg), files, template.Id);
  }

  public async getTemplateId(person: PersonType, message: { isSentByMe?: boolean; id?: string; text?: string }) {
    if (!message.isSentByMe || !message.id || !message.text || !this.user?.trackMessageTemplateId) {
      return undefined;
    }
    if (this.messageTemplateId.has(message.id)) {
      return this.messageTemplateId.get(message.id);
    }
    try {
      const { templates } = await this.getData({ person });
      for (let i = templates.length - 1; i >= 0; i--) {
        const template = templates[i];
        const similarity = (template.msg.length - distance(template.msg, message.text)) / template.msg.length;
        if (similarity >= TEMPLATE_SIMILARITY_THRESHOLD) {
          this.messageTemplateId.set(message.id, template.Id);
          return template.Id;
        }
      }
    } catch (err) {
      this.logger.error({ err }, "Failed to get template id of a message");
    }
  }

  public openPreviewMsg(template: TemplateType) {
    this.previewdTemplate = template;
  }

  public closePreviewMsg() {
    this.previewdTemplate = undefined;
  }

  public closeTemplateWidget() {
    this.isOpen = false;
    this.previewdTemplate = undefined;
  }

  private async getFiles(templateId: string, templateFiles?: { name?: string; url: string }[]) {
    if (!templateFiles?.length) {
      return;
    }

    const createFile = async (templateFile: { name?: string; url: string }, index: number) => {
      try {
        const response = await backgroundClient.send<typeof BgCommands.HTTP_REQUEST, string>(BgCommands.HTTP_REQUEST, {
          responseType: "base64",
          fullUrl: templateFile.url,
          method: "GET",
        });
        const { base64, type } = dataUriToBase64(response);
        if (!base64 || !type) {
          return;
        }
        const fileNgix = njixTypes[type as keyof typeof njixTypes]?.extensions[0] || type.split("/")[1];
        const fileName = templateFile.name || extractFilenameFromUrl(templateFile.url) || `tuvis_${index}.${fileNgix}`;
        return new File([dataURItoBlob(base64, type)], fileName, { type });
      } catch (err) {
        this.logger.error({ err }, "failed to create image file for template");
        return;
      }
    };

    const files =
      this.templateFilesCache.get(templateId) ||
      ((await Promise.all(templateFiles.map((templateFile, index) => createFile(templateFile, index))).catch(() => [])).filter(Boolean) as File[]);
    this.templateFilesCache.set(templateId, files);
    return files;
  }

  // avoid using await in this function prior to reading and writing to the cache
  // to avoid cache misses due to race conditions
  // since we cahce here the request and not the response
  // i.e everything needs to be syncronous until the cache write
  private async getData({
    person,
    group,
    showOnlyOwnByMe,
    isGroup,
  }: {
    person?: PersonType;
    group?: WhatsllyGroupType;
    showOnlyOwnByMe?: boolean;
    isGroup?: boolean;
  }): Promise<TemplateStoreData> {
    if ((!person && !isGroup) || !this.user) {
      throw new Error("UPGRADE");
    }
    if (this.user.freeUser) {
      throw new Error("UPGRADE");
    }
    if (!this.user.apex) {
      throw new Error("MISSING_APEX");
    }

    this.templatesCache.forEach(({ fetchedAt }, personId) => {
      if (fetchedAt < Date.now() - CACHE_TTL) {
        this.templatesCache.delete(personId);
      }
    });
    const cacheKey = (person?.Id || person?.originalCaller || group?.Id) ?? "Unknown";
    if (!this.templatesCache.has(cacheKey)) {
      const urlSearchParams = new URLSearchParams();
      if (showOnlyOwnByMe) {
        urlSearchParams.set("showOnlyOwnByMe", "true");
      }
      if (person?.Id) {
        urlSearchParams.set("personId", person.Id);
      }
      if (typeof person?.Account?.Id === "string") {
        urlSearchParams.set("accountId", person.Account?.Id);
      }
      const request = this.storage.get(STORAGE.LOGIN_PROVIDER).then((loginProvider) => {
        if (loginProvider !== AppIds.SALESFORCE) {
          return { success: true, templates: [], instanceUrl: "" } as GetTemplatesResponseType;
        }
        const { urlSuffix } = getConfigFromLoginProvider(loginProvider || undefined, "templates");
        return backgroundClient.send<typeof BgCommands.HTTP_REQUEST, GetTemplatesResponseType>(BgCommands.HTTP_REQUEST, {
          method: "GET",
          urlSuffix: `${urlSuffix}?${urlSearchParams.toString()}`,
        });
      });
      this.templatesCache.set(cacheKey, { request, fetchedAt: Date.now() });
    }
    const res = await this.templatesCache.get(cacheKey)!.request;
    if (!res || !res?.success) {
      throw Object.assign(new Error("NO_TEMPLATES"));
    }
    if (!res?.templates.length) {
      throw Object.assign(new Error("NO_TEMPLATES"), {
        templateEditorUrl: TemplatesStore.getTemplateEditorUrl(res),
      });
    }
    return {
      templatesUrl: TemplatesStore.getTemplateListUrl(res),
      templateEditorUrl: TemplatesStore.getTemplateEditorUrl(res),
      os: res.os,
      templates: res.templates,
    } as TemplateStoreData;
  }

  public getTemplates() {
    return this.getData({ person: this.person, showOnlyOwnByMe: this.showOnlyOwnByMe, group: this.group, isGroup: this.isGroup });
  }

  public get templatesData() {
    const promise =
      this.isOpen && this.forceUpdate ? this.getTemplates() : Promise.resolve({ templates: [], os: "", templateEditorUrl: "", templatesUrl: "" });
    return fromPromise(promise);
  }

  public get previewTemplate() {
    return Boolean(this.previewdTemplate);
  }

  public get allowCreateTemplate() {
    return Boolean(this.user?.templates?.add);
  }

  public get allowEditTemplate() {
    return Boolean(this.user?.templates?.edit);
  }

  private static getTemplateEditorUrl({ instanceUrl, templateEditorUrl }: { instanceUrl: string; templateEditorUrl?: string }) {
    if (templateEditorUrl) {
      return templateEditorUrl;
    }
    return `${instanceUrl}/apex/Whatslly_Template_Editor`;
  }

  private static getTemplateListUrl({ instanceUrl, templatesUrl }: { instanceUrl: string; templatesUrl?: string }) {
    if (templatesUrl) {
      return templatesUrl;
    }
    return `${instanceUrl}/apex/Whatslly_Templates`;
  }

  async toggleFavoriteTemplate(templateId: string) {
    this.favoriteTemplates.has(templateId) ? this.favoriteTemplates.delete(templateId) : this.favoriteTemplates.add(templateId);
    this.favoriteTemplates = new Set(this.favoriteTemplates);
    await this.storage.set(STORAGE.FAVORITE_TEMPLATES, Array.from(this.favoriteTemplates));
  }

  private async favoriteTemplatesInit() {
    const storageFavorites = await this.storage.get(STORAGE.FAVORITE_TEMPLATES);
    try {
      this.favoriteTemplates = new Set(storageFavorites || []);
    } catch (error) {
      this.favoriteTemplates = new Set();
    }
  }

  get containerSizeIsBig() {
    return this._containerSizeIsBig;
  }
  set containerSizeIsBig(newState: boolean) {
    this._containerSizeIsBig = newState;
  }

  get height() {
    if (!this.isOpen) {
      return SizeConstants.CLOSED_TEMPLATES_SIZE;
    }
    return this.containerSizeIsBig ? SizeConstants.BIG_TEMPLATES_SIZE : SizeConstants.SMALL_TEMPLATES_SIZE;
  }

  get whatsappPeddingBottomElement() {
    const whatsappMainChildDivs = document.querySelectorAll("#main>div");
    if (!whatsappMainChildDivs) {
      return;
    }
    const lastDivInWhatsappMain = whatsappMainChildDivs[whatsappMainChildDivs.length - 1];
    if (lastDivInWhatsappMain) {
      (lastDivInWhatsappMain as HTMLDivElement).style.transition = "min-height 0.5s ease-in-out";
      return lastDivInWhatsappMain as HTMLDivElement;
    }
    return;
  }

  private updateWhatsappPeddingBottomElementHeight(newHeight: string) {
    const wapElement = this.whatsappPeddingBottomElement;
    if (wapElement) {
      wapElement.style.minHeight = newHeight;
    }
  }
}
